import { useCallback, useState, type ReactElement } from 'react';
import { useI18n } from '../lib/i18n/I18nProvider';
import { useSession, type OtpStartResult } from '../lib/auth/session';
import { Field, Select } from '../ui/primitives';
import { ErrorState } from '../ui/states';
import { BrandLogo } from '../ui/BrandLogo';

function AuthFrame({ title, subtitle, children }: { title: string; subtitle: string; children: ReactElement }) {
  const { t, toggleLocale, dir } = useI18n();
  return (
    <div className="auth-page" dir={dir}>
      <div className="auth-card">
        <div className="auth-card__brand">
          <BrandLogo alt={t('brand.name')} />
          <p className="muted">{t('brand.tagline')}</p>
        </div>
        <div>
          <h1>{title}</h1>
          <p className="muted">{subtitle}</p>
        </div>
        {children}
        <button type="button" className="btn btn--secondary btn--small" onClick={toggleLocale}>
          {t('lang.toggle')}
        </button>
      </div>
    </div>
  );
}

export function StaffSignIn(): ReactElement {
  const { t, locale } = useI18n();
  const session = useSession();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [useOtp, setUseOtp] = useState(false);
  const [captcha, setCaptcha] = useState('');
  const [started, setStarted] = useState<OtpStartResult | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submitPassword = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await session.passwordLogin({ phoneE164: phone, password });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }, [session, phone, password]);

  const startOtp = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setStarted(await session.otpStart({ phoneE164: phone, purpose: 'login', locale, captchaToken: captcha }));
    } catch (e) { setError(e); } finally { setBusy(false); }
  }, [session, phone, locale, captcha]);

  const verifyOtp = useCallback(async () => {
    if (!started) return;
    setBusy(true);
    setError(null);
    try { await session.otpVerify(started.otp_id, code); }
    catch (e) { setError(e); }
    finally { setBusy(false); }
  }, [session, started, code]);

  return (
    <AuthFrame title={t('auth.staff.title')} subtitle={t('auth.staff.subtitle')}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void (useOtp ? (started ? verifyOtp() : startOtp()) : submitPassword());
        }}
      >
        <Field
          label={t('auth.phone')}
          name="phone_e164"
          type="tel"
          value={phone}
          onChange={setPhone}
          required
          ltr
          placeholder="+9665XXXXXXXX"
          autoComplete="username"
        />
        {useOtp ? (
          started ? (
            <Field label={t('auth.code')} name="code" value={code} onChange={setCode} required ltr autoComplete="one-time-code" />
          ) : (
            <Field label={t('auth.captcha')} name="captcha_token" value={captcha} onChange={setCaptcha} hint={t('auth.captcha.hint')} ltr />
          )
        ) : (
          <Field label={t('auth.password')} name="password" type="password" value={password}
            onChange={setPassword} required ltr autoComplete="current-password" />
        )}
        {error ? <ErrorState error={error} /> : null}
        <button className="btn" type="submit" disabled={busy || !phone.trim() || (useOtp ? (started ? !code.trim() : false) : !password)}>
          {useOtp ? (started ? t('auth.verify') : t('auth.requestCode')) : t('auth.signIn')}
        </button>
        <button className="btn btn--secondary" type="button" onClick={() => { setUseOtp(!useOtp); setStarted(null); setCode(''); setError(null); }}>
          {useOtp ? t('auth.usePassword') : t('auth.useOtp')}
        </button>
      </form>
    </AuthFrame>
  );
}

/**
 * Customer sign-in: `POST /v1/auth/otp:request` then `:verify`, then — for a first-time customer —
 * `POST /v1/auth/register` with the one-shot registration token the verification returned.
 *
 * `captcha_token` is passed through: the server has required it since the progressive challenge
 * landed, and a client that drops it can challenge-lock a number.
 */
export function CustomerSignIn(): ReactElement {
  const { t, locale } = useI18n();
  const session = useSession();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [useOtp, setUseOtp] = useState(false);
  const [registrationIntent, setRegistrationIntent] = useState(false);
  const [captcha, setCaptcha] = useState('');
  const [started, setStarted] = useState<OtpStartResult | null>(null);
  const [code, setCode] = useState('');
  const [fullName, setFullName] = useState('');
  const [expoId, setExpoId] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [adult, setAdult] = useState(false);
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const start = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await session.otpStart({
        phoneE164: phone,
        purpose: registrationIntent ? 'register' : 'login',
        locale,
        captchaToken: captcha,
      });
      setStarted(res);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }, [session, phone, captcha, locale, registrationIntent]);

  const verify = useCallback(async () => {
    if (!started) return;
    setBusy(true);
    setError(null);
    try {
      await session.otpVerify(started.otp_id, code);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }, [session, started, code]);

  const finishRegistration = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await session.register({ fullName, password, locale, expoId });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }, [session, fullName, password, locale, expoId]);

  const passwordSignIn = useCallback(async () => {
    setBusy(true);
    setError(null);
    try { await session.passwordLogin({ phoneE164: phone, password }); }
    catch (e) { setError(e); }
    finally { setBusy(false); }
  }, [session, phone, password]);

  if (session.status === 'registration_required') {
    return (
      <AuthFrame title={t('auth.register.title')} subtitle={t('auth.register.hint')}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void finishRegistration();
          }}
        >
          <Field label={t('auth.firstName')} name="full_name" value={fullName} onChange={setFullName} required />
          <Field label={t('auth.password')} name="new_password" type="password" value={password}
            onChange={setPassword} required ltr autoComplete="new-password" hint={t('auth.password.hint')} />
          <Field label={t('auth.confirmPassword')} name="confirm_password" type="password"
            value={passwordConfirmation} onChange={setPasswordConfirmation} required ltr autoComplete="new-password" />
          {passwordConfirmation && password !== passwordConfirmation ? <p className="error">{t('auth.passwordMismatch')}</p> : null}
          <Field label={t('auth.expoId')} name="expo_id" value={expoId} onChange={setExpoId}
            required ltr hint={t('auth.expoId.hint')} />
          <p><a href={`https://moqeem.net/${locale}/legal/terms/current`} target="_blank" rel="noreferrer">{t('auth.readTerms')}</a></p>
          <p><a href={`https://moqeem.net/${locale}/legal/privacy/current`} target="_blank" rel="noreferrer">{t('auth.readPrivacy')}</a></p>
          <label><input type="checkbox" checked={adult} onChange={(e) => setAdult(e.target.checked)} /> {t('auth.ageConfirm')}</label>
          <label><input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} /> {t('auth.acceptTerms')}</label>
          <label><input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} /> {t('auth.acceptPrivacy')}</label>
          <Select
            label={t('auth.locale')}
            name="locale"
            value={locale}
            options={[
              { value: 'ar', label: 'العربية' },
              { value: 'en', label: 'English' },
            ]}
            onChange={() => undefined}
          />
          {error ? <ErrorState error={error} /> : null}
          <button className="btn" type="submit" disabled={busy || fullName.trim() === '' ||
            expoId.trim() === '' || password.length < 12 || password !== passwordConfirmation ||
            !adult || !terms || !privacy}>
            {t('auth.register')}
          </button>
        </form>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title={t('auth.customer.title')} subtitle={t('auth.customer.subtitle')}>
      {!useOtp ? (
        <form onSubmit={(e) => { e.preventDefault(); void passwordSignIn(); }}>
          <Field label={t('auth.phone')} name="phone_e164" type="tel" value={phone} onChange={setPhone}
            required ltr placeholder="+9665XXXXXXXX" autoComplete="username" />
          <Field label={t('auth.password')} name="password" type="password" value={password}
            onChange={setPassword} required ltr autoComplete="current-password" />
          {error ? <ErrorState error={error} /> : null}
          <button className="btn" type="submit" disabled={busy || !phone.trim() || !password}>{t('auth.signIn')}</button>
          <button className="btn btn--secondary" type="button" onClick={() => { setUseOtp(true); setError(null); }}>
            {t('auth.useOtp')}
          </button>
          <button className="btn btn--secondary" type="button" onClick={() => {
            setRegistrationIntent(true); setUseOtp(true); setStarted(null); setError(null);
          }}>
            {t('auth.createAccount')}
          </button>
        </form>
      ) : started === null ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void start();
          }}
        >
          <Field
            label={t('auth.phone')}
            name="phone_e164"
            type="tel"
            value={phone}
            onChange={setPhone}
            required
            ltr
            placeholder="+9665XXXXXXXX"
            autoComplete="tel"
          />
          <Field
            label={t('auth.captcha')}
            name="captcha_token"
            value={captcha}
            onChange={setCaptcha}
            hint={t('auth.captcha.hint')}
            ltr
          />
          {error ? <ErrorState error={error} /> : null}
          <button className="btn" type="submit" disabled={busy || phone.trim() === ''}>
            {t('auth.requestCode')}
          </button>
          <button className="btn btn--secondary" type="button" onClick={() => {
            setRegistrationIntent(false); setUseOtp(false); setError(null);
          }}>
            {t('auth.usePassword')}
          </button>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void verify();
          }}
        >
          <p className="muted">{t('auth.codeSent', { channel: started.channel })}</p>
          <p className="muted">{t('auth.resendIn', { seconds: started.resend_after_s })}</p>
          <Field
            label={t('auth.code')}
            name="code"
            value={code}
            onChange={setCode}
            required
            ltr
            autoComplete="one-time-code"
          />
          {error ? <ErrorState error={error} /> : null}
          <div className="row">
            <button className="btn" type="submit" disabled={busy || code.trim() === ''}>
              {t('auth.verify')}
            </button>
            <button
              className="btn btn--secondary"
              type="button"
              onClick={() => {
                setStarted(null);
                setCode('');
              }}
            >
              {t('auth.back')}
            </button>
          </div>
        </form>
      )}
    </AuthFrame>
  );
}
